import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Modal, Form, Input, message, Popconfirm } from 'antd';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const TextAssets = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { token } = useAuth();

  const columns = [
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Headline', dataIndex: 'headline', key: 'headline' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Actions', key: 'actions', width: 200, render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>Edit</Button>
          <Popconfirm title="Delete this text asset?" onConfirm={() => onDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    const load = async () => {
      try {
        if (!token) return;
        setLoading(true);
        const res = await fetch('/api/text-assets', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load text assets');
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        message.error(e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const onCreate = () => {
    form.resetFields();
    setOpen(true);
  };

  const onEdit = (record) => {
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onDelete = async (record) => {
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`/api/text-assets/${record.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      setRows(prev => prev.filter(r => r.id !== record.id));
      message.success('Text asset deleted');
    } catch (e) {
      message.error(e?.message || 'Delete failed');
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (!token) throw new Error('Not authenticated');
      if (values.id) {
        const res = await fetch(`/api/text-assets/${values.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ label: values.label, headline: values.headline, description: values.description }),
        });
        if (!res.ok) throw new Error('Failed to update');
        const updated = await res.json();
        setRows(prev => prev.map(r => r.id === updated.id ? updated : r));
        message.success('Text asset updated');
      } else {
        const res = await fetch('/api/text-assets', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ label: values.label, headline: values.headline, description: values.description }),
        });
        if (!res.ok) throw new Error('Failed to create');
        const created = await res.json();
        setRows(prev => [created, ...prev]);
        message.success('Text asset created');
      }
      setOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Creatives' }, { title: 'Text Assets' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Text Assets</Title>
        <Space>
          <Button type="primary" onClick={onCreate}>New Text Asset</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Manage headlines, descriptions, and text snippets.</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        open={open}
        title="Text Asset"
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="Label" name="label" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Headline" name="headline" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TextAssets;
